/**
 * VÉRAULT Portal — Advisor: Forms Admin (Phase 1)
 *
 * Two surfaces sit here:
 *
 *   1. **Documents** — the editable bodies that get embedded into the
 *      onboarding agreement bundle (Travel Planning Agreement + Terms &
 *      Conditions). Markdown-style textarea, versioned on save. The advisor
 *      pastes their real T&C here once; from then on every onboarding
 *      packet pulls the latest version.
 *
 *   2. **Section catalog** — read-only listing of the 14 sections (7 live in
 *      Phase 1, 7 stubbed for Phase 2). Lets the advisor see what's
 *      available without leaving the portal.
 *
 * Sending an actual packet still happens from a client's profile, not here —
 * this page is for shared resources and config.
 */
import React from "react";
import {
  ScrollText,
  FileText,
  Check,
  Loader2,
  AlertCircle,
} from "lucide-react";
import {
  Surface,
  Heading,
  EditorialRule,
  PageHeader,
  PortalButton,
  StatusTag,
  Skeleton,
  AIWhisper,
  Empty,
  api,
} from "../../index";

const CATEGORY_LABEL = {
  agreement: "Agreement bundle",
  onboarding: "Onboarding",
  preferences: "Preferences",
  logistics: "Logistics",
  post_trip: "Post-trip",
};

function DocumentEditor({ slug, allDocs, onSaved }) {
  const doc = allDocs.find((d) => d.slug === slug);
  const [content, setContent] = React.useState(doc?.content || "");
  const [title, setTitle] = React.useState(doc?.title || "");
  const [saving, setSaving] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setContent(doc?.content || "");
    setTitle(doc?.title || "");
  }, [doc?.version, doc?.content, doc?.title]);

  if (!doc) return <Skeleton className="h-64" />;

  const dirty = content !== doc.content || title !== doc.title;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.put(`/forms/documents/${slug}`, { title, content });
      onSaved(data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface variant="default" padded className="!p-6 md:!p-7 space-y-4" data-testid={`doc-editor-${slug}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            {slug === "planning_agreement" ? "Travel Planning Agreement" : "Terms & Conditions"} · v{doc.version}
          </p>
          <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">
            Last updated {new Date(doc.updated_at).toLocaleString()}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <StatusTag tone="gold">Unsaved changes</StatusTag>}
          {savedFlash && (
            <span className="font-accent italic text-[12.5px] text-[hsl(var(--accent-gold))]" data-testid={`doc-saved-${slug}`}>
              Saved · v{doc.version}
            </span>
          )}
          <PortalButton
            variant="primary"
            size="sm"
            icon={saving ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid={`doc-save-${slug}`}
          >
            {saving ? "Saving" : "Save changes"}
          </PortalButton>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded bg-[#FCEAE0] border border-[#F0C8A8] text-[13px] text-[#8B4513]">
          {error}
        </div>
      )}

      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5">
          Title
        </p>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 text-[14px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white"
          data-testid={`doc-title-${slug}`}
        />
      </div>

      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5">
          Content · markdown-friendly
        </p>
        <textarea
          rows={24}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Paste the full document body here. Markdown headings, bullets, and emphasis are preserved on the client side."
          className="w-full px-4 py-3 text-[14px] font-mono leading-relaxed bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white resize-none"
          data-testid={`doc-content-${slug}`}
        />
        <p className="mt-2 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
          The client sees this rendered with editorial styling. Use **bold**, *italic*, `# heading`, and `- list` markers.
        </p>
      </div>
    </Surface>
  );
}

function SectionCatalogList({ sections }) {
  const grouped = React.useMemo(() => {
    const g = {};
    sections.forEach((s) => {
      if (!g[s.category]) g[s.category] = [];
      g[s.category].push(s);
    });
    return g;
  }, [sections]);

  if (!sections.length) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-6" data-testid="section-catalog">
      {Object.keys(grouped).map((cat) => (
        <div key={cat}>
          <Heading level="eyebrow">{CATEGORY_LABEL[cat] || cat}</Heading>
          <ul className="mt-3 grid sm:grid-cols-2 gap-3">
            {grouped[cat].map((s) => (
              <li key={s.slug}>
                <Surface variant="paper" padded={false} className="!p-4 h-full" data-testid={`section-info-${s.slug}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-body text-[13.5px]">{s.name}</p>
                      <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-1 leading-relaxed">
                        {s.description}
                      </p>
                    </div>
                    {s.phase === 2 ? (
                      <StatusTag tone="neutral">Coming</StatusTag>
                    ) : (
                      <StatusTag tone="sage">Live</StatusTag>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                    {s.fields?.length ? `${s.fields.length} field${s.fields.length === 1 ? "" : "s"}` : "—"}
                    {s.requires_signature && <span>· Signature</span>}
                    {s.requires_ack && <span>· Acknowledgement</span>}
                  </div>
                </Surface>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function AdvisorForms() {
  const [documents, setDocuments] = React.useState(null);
  const [sections, setSections] = React.useState(null);
  const [tab, setTab] = React.useState("documents");

  const refreshDocs = React.useCallback(() => {
    api
      .get("/forms/documents")
      .then(({ data }) => setDocuments(Array.isArray(data) ? data : []))
      .catch(() => setDocuments([]));
  }, []);

  React.useEffect(() => {
    refreshDocs();
    api
      .get("/forms/sections")
      .then(({ data }) => setSections(Array.isArray(data) ? data : []))
      .catch(() => setSections([]));
  }, [refreshDocs]);

  const handleDocSaved = (updated) => {
    setDocuments((prev) => (prev || []).map((d) => (d.slug === updated.slug ? updated : d)));
  };

  return (
    <div className="space-y-8" data-testid="advisor-forms-page">
      <PageHeader
        eyebrow="Forms"
        title="Onboarding library."
        lede="Edit the agreement bundle once. Send the right sections from each client's profile."
      />

      <AIWhisper label="How this works" testid="forms-admin-whisper">
        Two pieces live here. The <strong className="font-medium">Documents</strong> below get embedded into every
        agreement-bundle packet you send — paste your real Travel Planning Agreement and Terms once and they'll be
        used everywhere. The <strong className="font-medium">Section catalog</strong> is the menu of building blocks
        you can mix into any client's intake from their profile.
      </AIWhisper>

      <div className="flex items-center gap-1.5 border-b border-[rgba(0,0,0,0.06)]">
        {[
          { id: "documents", label: "Documents", icon: ScrollText },
          { id: "catalog", label: "Section catalog", icon: FileText },
        ].map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`forms-admin-tab-${t.id}`}
              className={`px-4 py-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors border-b-2 -mb-px ${
                active
                  ? "text-[hsl(var(--foreground))] border-[hsl(var(--accent-gold))]"
                  : "text-[hsl(var(--foreground-muted))] border-transparent hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <Icon className="size-3" strokeWidth={1.5} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "documents" ? (
        documents === null ? (
          <Skeleton className="h-64" />
        ) : documents.length === 0 ? (
          <Empty title="Documents are loading" icon={<AlertCircle className="size-6" strokeWidth={1.25} />} />
        ) : (
          <div className="space-y-8">
            <DocumentEditor slug="planning_agreement" allDocs={documents} onSaved={handleDocSaved} />
            <EditorialRule label="" />
            <DocumentEditor slug="terms_and_conditions" allDocs={documents} onSaved={handleDocSaved} />
          </div>
        )
      ) : (
        <SectionCatalogList sections={sections || []} />
      )}
    </div>
  );
}
